import React from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

/**
 * The operator's guide, served from inside the application.
 *
 * Deliberately in-app rather than a wiki link: the people who need it are
 * already looking at the thing it describes, and a wiki that lives on another
 * host is exactly the page that is unreachable on the day the directory is
 * broken. It is also versioned with the code, so a screenshot cannot drift
 * away from the release it documents.
 */

/**
 * The intrinsic size of each screenshot.
 *
 * Without these the images are lazy-loaded into a zero-height box, so the page
 * grows as you scroll — measured: the document went from 7,844px to 15,846px
 * while scrolling once. Anchor links then land nowhere near their target,
 * because the browser jumps to where the section was BEFORE the images below
 * it loaded and pushed it down. Reserving the box makes the layout stable and
 * the anchors exact.
 */
const SHOT_SIZES = {
  'agents.png': [1280, 1000],
  'bulk-upload.png': [1280, 1281],
  'bulk-validate.png': [1280, 720],
  'bulk-schedule.png': [1280, 720],
  'bulk-sheets.png': [1280, 720],
  'bulk-run.png': [1280, 720],
  'columns.png': [1280, 1000],
  'dashboard.png': [1280, 1000],
  'gpo.png': [1280, 1000],
  'installer-agent.png': [1280, 1546],
  'installer-dc.png': [1280, 1486],
  'installer-enrolled.png': [1280, 1000],
  'ous.png': [1280, 1082],
  'trusts.png': [1280, 1072],
  'users.png': [1280, 1000],
};

const Shot = ({ src, alt, caption }) => {
  const [w, h] = SHOT_SIZES[src.split('/').pop()] || [1280, 1000];
  return (
  <Box sx={{ my: 2 }}>
    <Box
      component="img"
      src={src}
      alt={alt}
      loading="lazy"
      width={w}
      height={h}
      sx={{
        width: '100%',
        maxWidth: 980,
        height: 'auto',
        // Holds the box open before the bytes arrive, so nothing below moves.
        aspectRatio: `${w} / ${h}`,
        display: 'block',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.hover',
      }}
    />
    {caption && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {caption}
      </Typography>
    )}
  </Box>
  );
};

const Section = ({ id, title, children }) => (
  <Card variant="outlined" id={id} sx={{ mb: 3, scrollMarginTop: 80 }}>
    <CardContent>
      <Typography variant="h5" sx={{ mb: 1.5 }}>{title}</Typography>
      {children}
    </CardContent>
  </Card>
);

const P = ({ children, ...rest }) => (
  <Typography variant="body1" sx={{ mb: 1.5 }} {...rest}>{children}</Typography>
);

// Emphasis and literal values, as components rather than raw tags so they pick
// up the theme's colours in both light and dark mode. A bare <code> inherits
// nothing and turns invisible on a dark background.
const B = ({ children }) => (
  <Box component="strong" sx={{ fontWeight: 600 }}>{children}</Box>
);

const Code = ({ children }) => (
  <Box
    component="code"
    sx={{
      px: 0.6, py: 0.15, borderRadius: 0.5, mx: 0.25,
      bgcolor: 'action.hover', color: 'text.primary',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: '0.875em',
    }}
  >
    {children}
  </Box>
);

const CONTENTS = [
  ['what-it-is', 'What this is, and how it reaches your DCs'],
  ['signing-in', 'Signing in and choosing a domain'],
  ['lists', 'Working with the lists'],
  ['users', 'Users'],
  ['groups-ous', 'Groups and OUs'],
  ['gpo', 'Group Policy'],
  ['agents', 'Domain controllers and agents'],
  ['trusts', 'Trusts between domains'],
  ['installers', 'Building a new DC, and installing the agent'],
  ['bulk', 'Bulk user changes from a spreadsheet'],
  ['api', 'API keys and webhooks'],
  ['trouble', 'When something is wrong'],
];

/**
 * Jump to a section.
 *
 * Done in JS rather than left to the browser's own anchor handling: the URL is
 * still updated so the link is shareable and the back button works, but the
 * scroll is performed against the element as it is NOW. Native handling
 * computes the target once, which is wrong on a page whose images are still
 * settling — and it also scrolls the window, which is not always the element
 * that scrolls.
 */
function jumpTo(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  // Keep the hash for sharing and history, without a second jump.
  if (window.history?.replaceState) {
    window.history.replaceState(null, '', `#${id}`);
  }
}

function Guide() {
  // Hiding the menu item is presentation; this is the actual gate. A
  // non-administrator who types the URL gets the refusal, not the page.
  const user = useSelector((state) => state.auth.user);
  const isAdmin = Boolean(user?.is_staff || user?.is_superuser);

  // A link straight to /guide#api should land on the right section, so do the
  // same deliberate scroll once the page has mounted.
  React.useEffect(() => {
    const id = window.location.hash.replace('#', '');
    if (!id) return;
    const t = setTimeout(() => jumpTo(id), 150);
    return () => clearTimeout(t);
  }, []);

  if (!isAdmin) {
    return (
      <Alert severity="warning" sx={{ maxWidth: 640 }}>
        <AlertTitle>Administrators only</AlertTitle>
        This guide covers enrollment, installers and trusts, which are
        administrator actions. Ask an administrator if you need it.
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 1040 }}>
      <Typography variant="h4" gutterBottom>User guide</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        How to run this application, and the handful of Samba facts that explain
        why it behaves the way it does.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Contents</Typography>
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
          {CONTENTS.map(([id, title]) => (
            <Chip
              key={id}
              label={title}
              size="small"
              variant="outlined"
              component="a"
              href={`#${id}`}
              clickable
              onClick={(e) => { e.preventDefault(); jumpTo(id); }}
            />
          ))}
        </Stack>
      </Paper>

      <Section id="what-it-is" title="What this is, and how it reaches your DCs">
        <P>
          This application manages one or more Samba 4 Active Directory domain
          controllers. It does <strong>not</strong> log in to them. Each DC runs a
          small agent that dials out to this hub, and the hub sends it a
          <em> named operation</em> — "list users", "create group" — which the agent
          checks against its own allowlist before doing anything. There is no shell,
          and the application holds no password that can be replayed against a DC.
        </P>
        <P>
          Two consequences you will notice. A DC that is switched off simply shows as
          offline rather than breaking the page. And anything the agent has not been
          taught to do cannot be done from here at all, however you phrase it.
        </P>
        <Shot src="/guide-img/dashboard.png" alt="The dashboard"
              caption="The dashboard summarises the domain currently selected." />
      </Section>

      <Section id="signing-in" title="Signing in and choosing a domain">
        <P>
          Sign in with your application account — this is separate from any directory
          account. Once in, the control that matters most is the <strong>domain
          selector</strong> in the top bar, next to the word DOMAIN.
        </P>
        <Alert severity="info" sx={{ mb: 1.5 }}>
          <AlertTitle>The selector is not cosmetic</AlertTitle>
          It decides which domain controller every page reads from and writes to.
          Change it and the cached directory data is cleared, so the next page you
          open genuinely comes from the DC you picked — not the previous one's users
          for the next few minutes.
        </Alert>
        <P>
          Every list shows a <em>Reading from</em> badge naming the DC and realm the
          rows came from, so you are never guessing which domain you are looking at.
        </P>
      </Section>

      <Section id="lists" title="Working with the lists">
        <P>
          Users, Groups, OUs and Group Policies share the same controls.
        </P>
        <List dense>
          <ListItem><ListItemText
            primary="Columns"
            secondary="Choose which columns to show and drag their order with the arrows. Your choice is remembered in this browser, per list. Reset puts it back." /></ListItem>
          <ListItem><ListItemText
            primary="Sorting"
            secondary="Click a column heading. Dates sort chronologically, not alphabetically, and rows with no date sort last rather than pretending to be the oldest." /></ListItem>
          <ListItem><ListItemText
            primary="Pagination"
            secondary="Below each table, with a page size you can change." /></ListItem>
          <ListItem><ListItemText
            primary="Created"
            secondary="The date the DIRECTORY created the object. A dash means that DC did not report one — usually an agent older than the release that started asking." /></ListItem>
        </List>
        <Shot src="/guide-img/columns.png" alt="The column picker"
              caption="Tick to show, arrows to reorder. The last visible column cannot be hidden." />
      </Section>

      <Section id="users" title="Users">
        <P>
          Create, edit, reset a password, enable or disable, and delete. The
          <strong> Status</strong> column shows whether the account is enabled;
          disabling is preferred to deleting when someone leaves, because it keeps
          the SID and therefore their access history.
        </P>
        <P>
          <strong>Created after / Created before</strong> filter on the directory's
          own creation date. Both bounds are inclusive and either works alone, so
          "everything made since Monday" is one field. This filter is applied by the
          server across the whole domain — unlike the search box, which narrows the
          rows already on screen.
        </P>
        <Shot src="/guide-img/users.png" alt="The users list"
              caption="Users, with the status and created columns and the date filters." />
      </Section>

      <Section id="groups-ous" title="Groups and OUs">
        <P>
          Groups list their members and let you add and remove them. OUs show the
          full distinguished name, which is what you will need when moving objects
          or linking a policy.
        </P>
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Deleting an OU that still contains objects will fail. Move or delete the
          contents first — the error will tell you, but it is quicker to know.
        </Alert>
        <Shot src="/guide-img/ous.png" alt="Organizational units" />
      </Section>

      <Section id="gpo" title="Group Policy">
        <P>
          Lists every Group Policy Object with its GUID, display name, sysvol path
          and status. <strong>Status</strong> comes from the policy's own flags:
          <em> enabled</em> means both halves apply; a GPO can have its user or
          computer half disabled, or both, in which case it does nothing.
        </P>
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          <AlertTitle>Samba does not replicate sysvol</AlertTitle>
          The directory half of a GPO replicates between DCs; the FILES in sysvol do
          not. After adding a DC you must sync sysvol yourself, or policies will
          appear in this list and never apply. The DC installer prints the command.
        </Alert>
        <Shot src="/guide-img/gpo.png" alt="Group Policy objects" />
      </Section>

      <Section id="agents" title="Domain controllers and agents">
        <P>
          The <strong>Agents</strong> page lists every enrolled DC: whether it is
          online, its realm, base DN, Samba version and when it last checked in. You
          can rename one here — the name is a label for you, and renaming does not
          break its identity, which is tied to its certificate.
        </P>
        <P>
          From each DC you can start, restart and stop its directory service, and
          issue a fresh enrollment token if you are rebuilding it.
        </P>
        <Shot src="/guide-img/agents.png" alt="The agents page" />
      </Section>

      <Section id="trusts" title="Trusts between domains">
        <P>
          The <strong>Trusts</strong> tab shows the trusts held by the selected DC,
          with the type, direction, whether SID filtering is on, the addresses each
          side uses, when it was established and who last validated it.
        </P>
        <P>
          Where this application did not create the trust it says so rather than
          inventing a provenance it does not have. <em>Validate</em> re-checks it and
          records the result.
        </P>
        <Alert severity="info" sx={{ mb: 1.5 }}>
          A trust needs the two domains to be able to reach each other on the
          network. Establishing one between separate organisations therefore means
          connecting the networks first — the trust itself does not create a path.
        </Alert>
        <Shot src="/guide-img/trusts.png" alt="Trust details" />
      </Section>

      <Section id="installers" title="Building a new DC, and installing the agent">
        <P>
          <strong>Installers</strong> generates a ready-to-run script with your
          values already substituted, for Ubuntu LTS and for RHEL and its rebuilds.
        </P>

        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>A new domain controller</Typography>
        <P>
          Choose <em>Samba 4 AD Domain Controller</em>, pick whether you are creating
          a new forest or joining an existing domain as a replica, fill in the realm
          and credentials, and download.
        </P>
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          <AlertTitle>Two things that will otherwise bite you</AlertTitle>
          A Samba AD DC <strong>cannot run in an unprivileged container</strong> — it
          stores NT ACLs in extended attributes such a container may not write. Use a
          privileged container or a VM; the script checks before it touches anything.
          And on RHEL, Red Hat ships Samba <strong>without</strong> AD DC support, so
          the base package installs and then cannot provision — use the opt-in
          repository the form offers, or supply your own build.
        </Alert>
        <Shot src="/guide-img/installer-dc.png" alt="The DC installer form" />

        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>The agent, in three steps</Typography>
        <P>
          Choose <em>samba-webui management agent</em>. You do not need to visit the
          Agents page first:
        </P>
        <List dense>
          <ListItem><ListItemText
            primary="Step 1 — name the DC"
            secondary="A new name creates it and issues a token; an existing name re-issues, which is what you want for a re-install. The hub URL and enrollment bundle are filled in for you, and the CA fingerprint is shown so you can compare it with what the agent prints on first start." /></ListItem>
          <ListItem><ListItemText
            primary="Step 2 — check the values"
            secondary="Especially the read-only bind account and its password: without the password the agent will not start at all." /></ListItem>
          <ListItem><ListItemText
            primary="Step 3 — run it on the DC"
            secondary="As root, on the machine that is already a domain controller." /></ListItem>
        </List>
        <Shot src="/guide-img/installer-agent.png" alt="The agent installer" />
        <Shot src="/guide-img/installer-enrolled.png" alt="After enrolling"
              caption="After Step 1 the hub URL and bundle are filled in, with the fingerprint to compare." />
        <Alert severity="warning">
          The downloaded file contains your values <strong>in clear</strong>,
          including passwords. Copy it to the target machine, run it, delete it.
          The enrollment bundle is single-use and is consumed at first start.
        </Alert>
      </Section>

      <Section id="bulk" title="Bulk user changes from a spreadsheet">
        <P>
          <B>Bulk operations</B> takes a CSV and creates, enables or disables
          accounts — straight away, or on a schedule you set. Each row names the
          domain controller it applies to, so one sheet can create this month's
          joiners on one DC and disable its leavers on another. That is not a
          convenience; a joiner and a leaver on the same day usually <i>are</i> one
          change, and splitting them across two files is how one half gets
          forgotten.
        </P>

        <P><B>1. Get the template.</B> Download it from the page. It is generated
          from the same column definition the validator reads, so a template you
          saved months ago cannot be rejected by a validator that has moved on.
          The page also lists the exact agent names you are allowed to put in the
          <Code>agent</Code> column — they are the DCs currently enrolled.
        </P>

        <Shot src="/guide-img/bulk-upload.png"
              alt="The bulk operations page showing the template download and the column table"
              caption="Every column, what it means, and which actions it applies to." />

        <P><B>2. Check it before you commit to it.</B> Upload the filled sheet
          and press Validate. Nothing is stored and nothing is sent to a domain
          controller. Every row is checked — a DC that is not enrolled, a
          username with characters AD will not accept, the same user told to do
          two contradictory things — and you see exactly what <i>would</i> happen.
          This is the step that saves you: finding "row 41 names a DC that does
          not exist" now is a great deal better than finding it at 02:00 tomorrow.
        </P>

        <Shot src="/guide-img/bulk-validate.png"
              alt="Validation result listing usable rows and errors"
              caption="Six usable rows across two DCs, with a per-agent summary." />

        <P>
          One pairing is deliberately allowed: <Code>create</Code> and then
          <Code>disable</Code> for the same person in the same sheet. That is the
          normal way to prepare an account before someone's start date, and rows
          are applied in file order. <Code>enable</Code> and <Code>disable</Code>
          for the same person is refused, because whichever ran last would win
          and you would never know which.
        </P>

        <P><B>3. Say when.</B> Manual means it waits for you. Otherwise pick
          once, hourly, daily, weekly, monthly, or write a cron expression. The
          timezone is yours to set, and a rule like "02:00 daily" keeps meaning
          02:00 local across a daylight-saving change rather than drifting by an
          hour.
        </P>

        <Shot src="/guide-img/bulk-schedule.png"
              alt="The schedule controls, set to every Monday at 02:00 New York time"
              caption="Presets for the common cases; cron for the rest." />

        <P>
          Stored sheets keep their rows, so running one again applies it again.
          The flask icon is a <B>dry run</B> — every row walked, nothing sent —
          which is worth using on a sheet stored weeks ago, because a DC may have
          been revoked since.
        </P>

        <Shot src="/guide-img/bulk-sheets.png"
              alt="Stored sheets with schedule, next run and last result"
              caption="Next run, last result, and a switch to pause a schedule." />

        <P><B>About passwords.</B> Leave the <Code>password</Code> column blank
          and one is generated when the row runs, shown to you <B>once</B>, and
          then destroyed. Ask for them a second time and you get a refusal, not
          the passwords — if they are lost, reset them individually. You may
          supply your own passwords instead, but then you have a file full of
          credentials sitting in a downloads folder, which is the worse outcome
          of the two.
        </P>

        <P>
          Every run records the outcome of every row, not just the totals, and
          each row produces its own entry in the audit trail. "14 of 200 failed"
          without naming which 14 is not something anyone can act on.
        </P>

        <Shot src="/guide-img/bulk-run.png"
              alt="A run result listing the outcome of every row"
              caption="Per-row outcomes, including the reason for anything refused." />

        <P>
          <B>One thing that will refuse rather than guess.</B> If a row asks for
          an <Code>ou</Code> and the agent on that DC is older than 0.4.0, the
          row is refused. An older agent ignores the argument silently, and
          200 accounts quietly landing in the default Users container is a much
          worse outcome than an error telling you to update the agent.
        </P>
      </Section>

      <Section id="api" title="API keys and webhooks">
        <P>
          Administrators can mint API keys for scripts and other systems. A key is
          shown <strong>once</strong> and stored only as a hash — if you lose it,
          issue a new one. A key can be read-only, and can be pinned to a single
          domain controller, in which case it cannot be pointed at another.
        </P>
        <P>
          Webhooks fire when directory objects change, so another system can react
          without polling. Each delivery is signed, and failures are retried a few
          times before the endpoint is marked unhealthy.
        </P>
      </Section>

      <Section id="trouble" title="When something is wrong">
        <List dense>
          <ListItem><ListItemText
            primary="A DC shows offline"
            secondary="The agent dials out, so check it is running on that DC (systemctl status samba-agent) and that it can reach this hub. Run `samba-agent doctor` on the DC: it checks the config, the LDAP bind, its sudo grants and its certificate, and names what is wrong." /></ListItem>
          <ListItem><ListItemText
            primary="A list is empty or stale"
            secondary="Use Refresh on the page, which bypasses the cache. Confirm the domain selector is on the DC you mean." /></ListItem>
          <ListItem><ListItemText
            primary="A write fails but reads work"
            secondary="Almost always the agent's sudo grants. `samba-agent doctor` reports them; run it as root and it will report on the agent's account rather than yours." /></ListItem>
          <ListItem><ListItemText
            primary="A new replica DC will not replicate"
            secondary="Check the EXISTING DC first. If its `server services` setting is missing kcc, no replication topology is ever built and the new DC can never become a partner — that is a fault on the old machine, not the new one." /></ListItem>
          <ListItem><ListItemText
            primary="Diagnostics"
            secondary="The Diagnostics page runs checks against the selected DC and shows the raw output, which is usually faster than reasoning about it from here." /></ListItem>
        </List>
      </Section>

      <Divider sx={{ my: 3 }} />
      <Typography variant="caption" color="text.secondary">
        Screenshots are of this application and are updated with the release.
        Source and issues:{' '}
        <Link href="https://git.ravricsolutions.com/rbenlizar/samba-webui" target="_blank" rel="noopener">
          samba-webui
        </Link>.
      </Typography>
    </Box>
  );
}

export default Guide;
